import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type PropsWithChildren,
} from "react";

import { intersection } from "lodash";

import { type TableSelection } from "@devexpress/dx-react-grid-bootstrap4";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { CustomCheckbox } from "@shared/custom-checkbox";

import { usePerformanceTrackingChartView } from "../../../hooks/use-chart-state";
import { usePerformanceTrackingTableSelectionState } from "../../../hooks/use-selection-state";
import { useAllSelectedRowIds } from "../../contexts";
import { type TableRowType } from "../performance-tracking-table";

function recusiveRemove(row: TableRowType, setToRemove: Set<string>) {
  setToRemove.delete(row.key);
  row.children?.forEach((child) => {
    recusiveRemove(child, setToRemove);
  });
}

function getChildrenKeys(row: TableRowType) {
  const keys: string[] = [];
  row.children?.forEach((child) => {
    keys.push(child.key);
    keys.push(...getChildrenKeys(child));
  });
  return keys;
}

export const TableSelectionCell = ({
  selected,
  row: _row,
  tableRow,
  tableColumn,
  ...restOfProps
}: PropsWithChildren<TableSelection.CellProps>) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);
  const [, setSelectionState] = usePerformanceTrackingTableSelectionState();
  const [allSelectedRowIds] = useAllSelectedRowIds();

  const row = _row as TableRowType;
  const checkboxRef = useRef<HTMLInputElement>(null);

  const [chartView] = usePerformanceTrackingChartView();
  const isSplitView = chartView === "split";

  const { designElementManager } = useDesignElementManager();

  const selectColor = isSplitView
    ? designElementManager.getColorForIndex(row.index)
    : "#7081FF"; // cosmic

  const isParent = tableRow.row.hasChildren;

  const isIntermediate = useMemo(() => {
    const allChildrenKeys = getChildrenKeys(row);

    const allSelectedChildRowIds = intersection(
      allChildrenKeys,
      allSelectedRowIds,
    );

    return (
      allSelectedChildRowIds.length !== allChildrenKeys.length &&
      allSelectedChildRowIds.length > 0
    );
  }, [allSelectedRowIds, row]);

  const isSelected = useMemo(() => {
    const allChildrenKeys = getChildrenKeys(row);

    const allSelectedChildRowIds = intersection(
      allChildrenKeys,
      allSelectedRowIds,
    );

    // if there are no children, i don't even need to think about it, just return what is selected
    if (!allChildrenKeys.length) {
      return selected;
    }

    if (!allSelectedChildRowIds.length) {
      return false;
    }
    if (allSelectedChildRowIds.length === allChildrenKeys.length) {
      return true;
    }

    // At this point, consider it true
    return true;
  }, [allSelectedRowIds, row, selected]);

  const onSelect = useCallback(() => {
    trackMMMEvent({
      component: TrackPerformanceTrackedComponent.PERFORMANCE_TRACKING_TABLE,
      action: "onChange",
      actionInfo: "Change selected channels",
    });

    setSelectionState((prev) => {
      const deselectionSet = new Set(prev.deselectedRowIds);
      const selectionSet = new Set(prev.selectedRowIds);

      if (isSelected) {
        deselectionSet.add(row.key);

        recusiveRemove(row, selectionSet);
      } else {
        selectionSet.add(row.key);

        recusiveRemove(row, deselectionSet);
      }

      return {
        deselectedRowIds: Array.from(deselectionSet),
        selectedRowIds: Array.from(selectionSet),
      };
    });
  }, [row, isSelected, setSelectionState]);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIntermediate;
    }
  }, [isIntermediate]);

  return (
    <td {...restOfProps} className="sticky left-0 z-2 dx-g-bs4-fixed-cell">
      <div
        className="flex items-center justify-center w-full h-full"
        onClick={onSelect}
      >
        <CustomCheckbox
          className="pointer-events-none"
          ref={checkboxRef}
          // if it is a parent, let it default out
          backgroundColor={isParent ? undefined : selectColor}
          checked={isSelected}
        />
      </div>
    </td>
  );
};
